// First-party participant-funnel tracking.
//
// Sits alongside linkedin-ad-conversion-tracking/ rather than inside it: the
// ad-facing events go to both, but only this one lands somewhere we can query.
// The two sets are no longer identical — the pre-flight steps
// (preflight_mic_granted, preflight_screen_granted) exist to find our own
// drop-offs and have no LinkedIn counterpart, because they optimise nothing
// about ad delivery.
// LinkedIn's copy is aggregate, subject to their attribution windows, blocked by
// ad blockers and tracking prevention, and reports "insufficient data" at the
// volumes we actually run at. Keep both — theirs feeds their delivery
// optimization, ours feeds us.
//
// Everything here is fire-and-forget and swallows its own failures. This code
// runs on a funnel we are paying for clicks to fill; it must never be able to
// break it.

import BACKEND_URL from "../config";

const VISITOR_KEY = "cia_visitor_id";
const ATTRIBUTION_KEY = "cia_attribution";
const SESSION_KEY = "cia_session_id";

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// localStorage throws in Safari private mode and when cookies are blocked
// entirely. Degrade to an in-memory id for the session rather than taking the
// page down with us.
const memory = {};

function readStore(store, key) {
  try {
    return store.getItem(key);
  } catch {
    return memory[key] ?? null;
  }
}

function writeStore(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    memory[key] = value;
  }
}

/**
 * The visitor's stable id.
 *
 * This identifies a *browser*, not a person: it does not survive incognito, a
 * second device, or Safari's storage eviction. That is fine for locating a
 * drop-off cliff across a few dozen visitors, and not fine as identity for
 * anything else.
 */
export function getVisitorId() {
  let id = readStore(window.localStorage, VISITOR_KEY);
  if (!id) {
    id = newId();
    writeStore(window.localStorage, VISITOR_KEY, id);
  }
  return id;
}

export function getSessionId() {
  let id = readStore(window.sessionStorage, SESSION_KEY);
  if (!id) {
    id = newId();
    writeStore(window.sessionStorage, SESSION_KEY, id);
  }
  return id;
}

/**
 * Detect mobile/tablet (MVP-friendly heuristic).
 *
 * Moved here verbatim from InterviewPage.js so the landing page and the
 * interview page cannot disagree about what counts as a phone. Two copies of
 * this heuristic would be worse than none: the landing page would wave someone
 * through that the interview page then blocks, which is the exact failure this
 * gate exists to stop.
 */
export function isMobileOrTabletDevice() {
  if (typeof navigator === "undefined") return false;

  const ua = (navigator.userAgent || "").toLowerCase();

  // Common mobile/tablet signals
  const isMobileUA =
    /iphone|ipod|ipad|android|mobile|tablet|silk|kindle|webos/.test(ua);

  // iPadOS can report as "Macintosh" but has touch points
  const isIpadOs =
    /macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;

  return isMobileUA || isIpadOs;
}

/**
 * Ad attribution, captured on FIRST TOUCH ONLY and then frozen.
 *
 * Two reasons this cannot be read later instead:
 *
 * 1. It would not be there. The landing page is five client-side STEP_* states
 *    on one URL, so by the payment step the query string is long gone.
 * 2. Last-touch would be wrong even when it works. Someone who arrives from an
 *    ad, leaves, and returns directly the next day must stay credited to the ad;
 *    overwriting would rewrite them to `referrer: null` and make the campaign
 *    look like it produced nothing.
 *
 * `utm_content` is the field that carries "which ad". No platform hands you the
 * creative at runtime — you encode it yourself in each creative's destination
 * URL. `li_fat_id` / `rdt_cid` are the platforms' own opaque click ids: they
 * identify neither the ad nor the person, and are stored only so a completion
 * can be replayed to their Conversions API later.
 */
export function getAttribution() {
  const stored = readStore(window.localStorage, ATTRIBUTION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // fall through and re-capture
    }
  }

  const params = new URLSearchParams(window.location.search);
  const attribution = {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_content: params.get("utm_content"),
    utm_term: params.get("utm_term"),
    li_fat_id: params.get("li_fat_id"),
    rdt_cid: params.get("rdt_cid"),
    referrer: document.referrer || null,
    landing_path: window.location.pathname,
    user_agent: navigator.userAgent || null,
  };
  writeStore(window.localStorage, ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
}

/**
 * Record one funnel step.
 *
 * @param {string} eventName  must match FUNNEL_EVENT_NAMES in
 *                            app/repositories/funnel.py — the server drops
 *                            anything else.
 * @param {object} properties step-specific detail. Include `payout_shown`
 *                            wherever a price is on screen: it records what the
 *                            visitor was actually offered at the moment they
 *                            decided, which is the only thing that makes a later
 *                            price comparison interpretable.
 */
export function trackFunnel(eventName, properties = {}, options = {}) {
  try {
    const payload = JSON.stringify({
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      event_name: eventName,
      interview_type_slug: options.slug || null,
      attribution: getAttribution(),
      is_mobile: isMobileOrTabletDevice(),
      properties,
    });

    const url = `${BACKEND_URL}/api/funnel/events`;

    // sendBeacon survives the page being closed or navigated away from, which
    // matters most for exactly the events we care about — the last one before
    // someone abandons. Same-origin only in production (BACKEND_URL is ""), so
    // fall back to fetch in dev where it is cross-origin.
    if (!BACKEND_URL && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Instrumentation must never break the funnel it measures.
  }
}
